/**
 * Takes the scans out of a Notebloc PDF (#56).
 *
 * Notebloc exports each doc as one PDF: page 1 is the front, page 2 the back. Each page
 * is an A4 sheet with the cropped scan placed on it as a JPEG, which is why the PDF looks
 * badly cropped when it is opened. The JPEG inside is the scan exactly as Joey cropped it,
 * so this hands back those JPEGs, one per page, and the white sheet is never drawn.
 *
 * It is a reader for that shape of PDF, not a general one: it follows the page tree to
 * each page's images and returns each page's largest JPEG. A PDF it cannot follow (a
 * compressed object index, which Notebloc's PDF 1.3 cannot have) falls back to every JPEG
 * in the file, in file order. Images stored in any other format are not read, and the
 * page says so rather than uploading a blank card.
 */

export class PdfScanError extends Error {}

class PdfName {
  readonly name: string;
  constructor(name: string) {
    this.name = name;
  }
}

class PdfRef {
  readonly ref: number;
  constructor(ref: number) {
    this.ref = ref;
  }
}

type PdfDict = Map<string, PdfValue>;
type PdfValue = number | boolean | null | string | PdfName | PdfRef | PdfDict | PdfValue[];

type PdfObject = { value: PdfValue; streamStart: number; streamEnd: number };

const DELIMITER = /[\s/[\]<>()%{}]/;

/** A value parser over the PDF's bytes, read as one character per byte. */
class Reader {
  readonly text: string;
  at: number;

  constructor(text: string, at: number) {
    this.text = text;
    this.at = at;
  }

  skipSpace() {
    for (;;) {
      while (this.at < this.text.length && /\s/.test(this.text[this.at])) this.at += 1;
      if (this.text[this.at] !== "%") return;
      while (this.at < this.text.length && !/[\r\n]/.test(this.text[this.at])) this.at += 1;
    }
  }

  value(): PdfValue {
    this.skipSpace();
    const text = this.text;
    const here = text[this.at];
    if (text.startsWith("<<", this.at)) return this.dict();
    if (here === "[") {
      this.at += 1;
      const items: PdfValue[] = [];
      for (;;) {
        this.skipSpace();
        if (text[this.at] === "]" || this.at >= text.length) {
          this.at += 1;
          return items;
        }
        items.push(this.value());
      }
    }
    if (here === "/") {
      const start = (this.at += 1);
      while (this.at < text.length && !DELIMITER.test(text[this.at])) this.at += 1;
      return new PdfName(text.slice(start, this.at));
    }
    if (here === "(") return this.literal();
    if (here === "<") {
      const end = text.indexOf(">", this.at);
      const hex = text.slice(this.at + 1, end);
      this.at = end + 1;
      return hex;
    }
    const number = /^[+-]?(\d+\.?\d*|\.\d+)/.exec(text.slice(this.at, this.at + 32));
    if (number) {
      this.at += number[0].length;
      // "12 0 R" is a reference to object 12.
      const ref = /^\s+\d+\s+R(?=[\s/[\]<>()%{}]|$)/.exec(text.slice(this.at, this.at + 32));
      if (ref && /^\d+$/.test(number[0])) {
        this.at += ref[0].length;
        return new PdfRef(Number(number[0]));
      }
      return Number(number[0]);
    }
    const word = /^[a-z]+/i.exec(text.slice(this.at, this.at + 16));
    if (word) this.at += word[0].length;
    else this.at += 1;
    if (word?.[0] === "true") return true;
    if (word?.[0] === "false") return false;
    return null;
  }

  dict(): PdfDict {
    this.at += 2;
    const dict: PdfDict = new Map();
    for (;;) {
      this.skipSpace();
      if (this.text.startsWith(">>", this.at) || this.at >= this.text.length) {
        this.at += 2;
        return dict;
      }
      const key = this.value();
      if (!isName(key)) continue;
      dict.set(key.name, this.value());
    }
  }

  literal(): string {
    let depth = 0;
    const start = this.at + 1;
    for (; this.at < this.text.length; this.at += 1) {
      const character = this.text[this.at];
      if (character === "\\") this.at += 1;
      else if (character === "(") depth += 1;
      else if (character === ")" && --depth === 0) break;
    }
    this.at += 1;
    return this.text.slice(start, this.at - 1);
  }
}

function isName(value: PdfValue | undefined): value is PdfName {
  return value instanceof PdfName;
}

function isRef(value: PdfValue | undefined): value is PdfRef {
  return value instanceof PdfRef;
}

function isDict(value: PdfValue | undefined): value is PdfDict {
  return value instanceof Map;
}

function nameOf(value: PdfValue | undefined) {
  return isName(value) ? value.name : "";
}

/**
 * Every object in the file by number. A later definition of a number replaces an earlier
 * one, which is how a PDF saved with changes appended says what changed.
 *
 * A stream's length is read from the dictionary when it is a plain number, so the scan
 * jumps over the binary scan data instead of reading it for things that look like
 * objects. When the length is a reference, the scan finds "endstream" instead.
 */
function readObjects(text: string) {
  const objects = new Map<number, PdfObject>();
  const header = /(\d+)\s+\d+\s+obj\b/g;
  let match: RegExpExecArray | null;
  while ((match = header.exec(text))) {
    const reader = new Reader(text, header.lastIndex);
    const value = reader.value();
    reader.skipSpace();
    let streamStart = -1;
    let streamEnd = -1;
    if (text.startsWith("stream", reader.at)) {
      streamStart = reader.at + 6;
      if (text[streamStart] === "\r") streamStart += 1;
      if (text[streamStart] === "\n") streamStart += 1;
      const length = isDict(value) ? value.get("Length") : undefined;
      const end = text.indexOf("endstream", typeof length === "number" ? streamStart + length : streamStart);
      streamEnd = typeof length === "number" ? streamStart + length : trimEol(text, end);
      header.lastIndex = end < 0 ? text.length : end + 9;
    }
    objects.set(Number(match[1]), { value, streamStart, streamEnd });
  }
  return objects;
}

function trimEol(text: string, end: number) {
  if (end < 0) return end;
  if (text[end - 1] === "\n") end -= 1;
  if (text[end - 1] === "\r") end -= 1;
  return end;
}

/**
 * The JPEG in each page of a Notebloc PDF, in page order.
 *
 * Throws PdfScanError when there is nothing it can use, with a message for the owner.
 */
export function extractPdfScans(bytes: Uint8Array): Uint8Array<ArrayBuffer>[] {
  const text = new TextDecoder("latin1").decode(bytes);
  if (!text.startsWith("%PDF")) throw new PdfScanError("This file is not a PDF.");
  const objects = readObjects(text);

  const resolve = (value: PdfValue | undefined): PdfValue | undefined =>
    isRef(value) ? objects.get(value.ref)?.value : value;

  const jpegOf = (ref: number) => {
    const object = objects.get(ref);
    if (!object || !isDict(object.value) || object.streamStart < 0) return null;
    const dict = object.value;
    if (nameOf(dict.get("Subtype")) !== "Image") return null;
    const filter = resolve(dict.get("Filter"));
    const filters = Array.isArray(filter) ? filter.map(nameOf) : [nameOf(filter)];
    if (filters.length !== 1 || filters[0] !== "DCTDecode") return null;
    let end = object.streamEnd;
    const length = resolve(dict.get("Length"));
    if (typeof length === "number") end = object.streamStart + length;
    // A copy, so the scan outlives the PDF's bytes instead of holding all of them.
    const jpeg = bytes.slice(object.streamStart, end);
    if (jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return null;
    const width = resolve(dict.get("Width"));
    const height = resolve(dict.get("Height"));
    return { jpeg, area: (typeof width === "number" ? width : 0) * (typeof height === "number" ? height : 0) };
  };

  /** The biggest JPEG a page's resources draw, looking one form deep for a wrapped image. */
  const pageScan = (resources: PdfValue | undefined, depth = 0): { jpeg: Uint8Array<ArrayBuffer>; area: number } | null => {
    const dict = resolve(resources);
    const xobjects = isDict(dict) ? resolve(dict.get("XObject")) : undefined;
    if (!isDict(xobjects)) return null;
    let best: { jpeg: Uint8Array<ArrayBuffer>; area: number } | null = null;
    for (const entry of xobjects.values()) {
      if (!isRef(entry)) continue;
      let found = jpegOf(entry.ref);
      const inner = objects.get(entry.ref)?.value;
      if (!found && depth < 2 && isDict(inner) && nameOf(inner.get("Subtype")) === "Form") found = pageScan(inner.get("Resources"), depth + 1);
      if (found && (!best || found.area > best.area)) best = found;
    }
    return best;
  };

  const scans: Uint8Array<ArrayBuffer>[] = [];
  const visited = new Set<number>();
  const walk = (node: PdfValue | undefined, inherited: PdfValue | undefined) => {
    if (!isRef(node) || visited.has(node.ref)) return;
    visited.add(node.ref);
    const dict = resolve(node);
    if (!isDict(dict)) return;
    const resources = dict.get("Resources") ?? inherited;
    if (nameOf(dict.get("Type")) === "Pages") {
      const kids = resolve(dict.get("Kids"));
      if (Array.isArray(kids)) kids.forEach((kid) => walk(kid, resources));
      return;
    }
    const scan = pageScan(resources);
    if (scan) scans.push(scan.jpeg);
  };

  const catalog = [...objects.values()].find((object) => isDict(object.value) && nameOf(object.value.get("Type")) === "Catalog");
  if (catalog && isDict(catalog.value)) walk(catalog.value.get("Pages"), undefined);

  if (scans.length === 0) {
    // No page tree to follow: every JPEG in the file, in the order it was written.
    const inFileOrder = [...objects.entries()].sort((a, b) => a[1].streamStart - b[1].streamStart);
    for (const [ref] of inFileOrder) {
      const found = jpegOf(ref);
      if (found) scans.push(found.jpeg);
    }
  }
  if (scans.length === 0) {
    throw new PdfScanError("No scans were found in this PDF. Export this card from Notebloc as photos instead.");
  }
  return scans;
}

/**
 * A JPEG's width and height, from its frame header (#65). The guess at what each page of a
 * mailing is starts from its shape: a letter page is taller than it is wide.
 *
 * Read from the JPEG rather than the PDF's own Width and Height, so it works the same for a
 * photo picked straight off the phone. Null when there is no frame header to read.
 */
export function jpegSize(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let at = 2;
  while (at + 4 <= bytes.length) {
    if (bytes[at] !== 0xff) return null;
    const marker = bytes[at + 1];
    // Fill bytes, and markers that carry no length.
    if (marker === 0xff) { at += 1; continue; }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) { at += 2; continue; }
    const length = (bytes[at + 2] << 8) | bytes[at + 3];
    // Start of frame: every SOFn except DHT (C4), JPG (C8) and DAC (CC).
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      if (at + 9 > bytes.length) return null;
      const height = (bytes[at + 5] << 8) | bytes[at + 6];
      const width = (bytes[at + 7] << 8) | bytes[at + 8];
      return width && height ? { width, height } : null;
    }
    if (marker === 0xda) return null; // the image data began without a frame header
    at += 2 + length;
  }
  return null;
}
